import { VerticalFormGroupComponent } from 'components/shared/FormGroup/VerticalFormGroupComponent';
import { PercentWithUnit } from 'components/shared/Percent/PercentWithUnit';
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type {
  PriceImprovementExpirationTime,
  PriceImprovementStatus,
} from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.model';
import type { ApiError } from 'utils/http/apiErrorHelper';

import { CreatingActions } from '../actions/Actions.Creating';
import { InitializedActions } from '../actions/Actions.Inilialized';
import { PostCreationActions } from '../actions/Actions.PostCreation';
import { ExpirationTimeSelector } from '../ExpirationTimeSelector';

export interface PriceImprovementRequestFormProps {
  status: PriceImprovementStatus;
  requestedTradingOffer: number | undefined;
  expirationTime: Nullable<PriceImprovementExpirationTime>;
  comment?: string;
  creationError?: ApiError;
  cancellationError?: ApiError;
  onCommentChanged: (comment?: string) => void;
  onRequestedTradingOfferChanged: (requestedTradingOffer?: number) => void;
  onExpirationTimeChanged: (expirationTime: Nullable<PriceImprovementExpirationTime>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onDiscard: () => void;
  onErrorDismissed: () => void;
}

export const PriceImprovementRequestForm: FC<PriceImprovementRequestFormProps> = ({
  comment,
  requestedTradingOffer,
  expirationTime,
  status,
  creationError,
  cancellationError,
  onSubmit,
  onCancel,
  onDiscard,
  onCommentChanged,
  onRequestedTradingOfferChanged,
  onExpirationTimeChanged,
  onErrorDismissed,
}) => {
  const { formatMessage } = useIntl();

  const [commentValue, setCommentValue] = useState(comment);

  const onCommentChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => setCommentValue(e.target.value), []);
  const onCommentBlur = useCallback(() => {
    onCommentChanged(commentValue || undefined);
  }, [commentValue, onCommentChanged]);

  useEffect(() => setCommentValue(comment), [comment]);

  return (
    <>
      <div className="row align-items-end mb-4">
        <VerticalFormGroupComponent
          inputId="requestedTradingOffer"
          labelText={formatMessage({ id: 'priceImprovement.requestedTradingOffer' })}
          className="col-4"
        >
          <PercentWithUnit
            e2e="price-negotiation-requestedTradingOffer"
            id="requestedTradingOffer"
            key="requestedTradingOffer"
            unit="%"
            value={requestedTradingOffer}
            precision={4}
            decimalPadding
            isDisabled={false}
            isReadOnly={false}
            onBlur={onRequestedTradingOfferChanged}
          />
        </VerticalFormGroupComponent>
        <VerticalFormGroupComponent
          inputId="expirationTime"
          labelText={formatMessage({ id: 'priceImprovement.expirationTime' })}
          className="col-8"
        >
          <ExpirationTimeSelector
            value={expirationTime}
            onSelect={onExpirationTimeChanged}
            e2e="price-negotiation-comment"
          />
        </VerticalFormGroupComponent>
      </div>
      <textarea
        id="comment"
        value={commentValue}
        onBlur={onCommentBlur}
        onChange={onCommentChange}
        className="form-control mb-4"
        data-e2e="price-negotiation-comment"
        placeholder={formatMessage({ id: 'priceImprovement.comment.placeholder' })}
        rows={3}
      />
      <div className="d-flex flex-row">
        {status === 'Initialized' && (
          <InitializedActions
            onDiscarded={onDiscard}
            onRequested={onSubmit}
            creationError={creationError}
            onDimissCreationError={onErrorDismissed}
          />
        )}
        {status === 'Creating' && <CreatingActions />}
        {isPostCreationStatus(status) && (
          <PostCreationActions
            onCancelled={onCancel}
            cancellationError={cancellationError}
            onDimissCancellationError={onErrorDismissed}
          />
        )}
      </div>
    </>
  );
};

PriceImprovementRequestForm.displayName = 'PriceImprovementRequestForm';

function isPostCreationStatus(status: PriceImprovementStatus) {
  return status === 'Created' || status === 'Cancelling';
}
